import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, MapPin, Sparkles } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorState } from '@/components/ui/ErrorState';
import { EmptyState } from '@/components/ui/EmptyState';
import { KPICard } from '@/components/charts/KPICard';
import { LineChart } from '@/components/charts/LineChart';
import {
  ChartModeToggle,
  type ChartGranularity,
  type SeasonalityMode,
} from '@/components/charts/ChartModeToggle';
import { bucketBySeasonality, granularityForSeasonality } from '@/components/charts/seasonality';
import { SnapshotMenu } from '@/components/system/SnapshotMenu';
import { ForecastChart } from '@/components/products/ForecastChart';
import { ProductDrillTables } from '@/components/products/ProductDrillDrawer';
import { WardChoroplethMap, formatWardMetric } from '@/components/maps/WardChoroplethMap';
import {
  useProductForecast,
  useProductPerformance,
  useProductTimeseries,
  useProductWardSales,
  useWardsGeo,
} from '@/api/bi';
import { useFilters, rangeISO } from '@/state/filters';
import { fmt } from '@/lib/format';

function pickGranularity(iso: { start: string; end: string }): ChartGranularity {
  const days = Math.max(1, Math.round((new Date(iso.end).getTime() - new Date(iso.start).getTime()) / 86_400_000));
  if (days <= 2) return 'hour';
  if (days <= 60) return 'day';
  return 'week';
}

export function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const productId = Number(id);
  const range = useFilters((s) => s.range);
  const storeId = useFilters((s) => s.storeId);
  const storeIds = useFilters((s) => s.storeIds);
  const timeRangeMode = useFilters((s) => s.timeRangeMode);
  const iso = useMemo(() => rangeISO(range, timeRangeMode), [range, timeRangeMode]);

  const [granularity, setGranularity] = useState<ChartGranularity>(pickGranularity(iso));
  const [seasonality, setSeasonality] = useState<SeasonalityMode>('none');
  const effectiveGranularity = granularityForSeasonality(seasonality) ?? granularity;

  const valid = Number.isFinite(productId) && productId > 0;
  const taxonomy = { storeId, storeIds, productIds: valid ? [productId] : [] };

  const perf = useProductPerformance({
    start: iso.start,
    end: iso.end,
    groupBy: 'product',
    limit: 1,
    enabled: valid,
    ...taxonomy,
  });
  const series = useProductTimeseries({
    start: iso.start,
    end: iso.end,
    granularity: effectiveGranularity,
    enabled: valid,
    ...taxonomy,
  });
  const forecast = useProductForecast({ horizon: 14, lookback: 90, enabled: valid, ...taxonomy });

  const wardsGeo = useWardsGeo({ enabled: valid });
  const wardSales = useProductWardSales({
    start: iso.start,
    end: iso.end,
    enabled: valid,
    ...taxonomy,
  });
  const [selectedWardId, setSelectedWardId] = useState<number | null>(null);
  const topWards = useMemo(
    () => [...(wardSales.data?.wards ?? [])].sort((a, b) => b.gmv - a.gmv).slice(0, 3),
    [wardSales.data],
  );

  const row = perf.data?.rows[0];
  const name = row?.name ?? `Product #${productId}`;

  const chartData = useMemo(
    () =>
      (series.data?.buckets ?? []).map((b) => ({
        t: b.t,
        gmv: b.gmv,
        units: b.units,
        orders: b.orders,
      })),
    [series.data],
  );

  const seasonalityChartData = useMemo(() => {
    if (seasonality === 'none') return [];
    return bucketBySeasonality(series.data?.buckets ?? [], {
      timeKey: 't',
      metrics: ['gmv', 'units', 'orders'],
      mode: seasonality,
      aggregate: 'sum',
    });
  }, [series.data, seasonality]);

  const forecastSummary = useMemo(() => {
    const fc = forecast.data?.forecast ?? [];
    return {
      projectedGmv: fc.reduce((s, p) => s + (p.gmv_yhat ?? 0), 0),
      projectedOrders: fc.reduce((s, p) => s + (p.orders_yhat ?? 0), 0),
      projectedUnits: fc.reduce((s, p) => s + p.yhat, 0),
    };
  }, [forecast.data]);

  const returnPath = `/products/${productId}`;

  if (!valid) {
    return (
      <Card>
        <CardBody>
          <EmptyState title="Invalid product" description="That product id doesn't look right." />
        </CardBody>
      </Card>
    );
  }

  return (
    <>
      <Link
        to="/products"
        className="press mb-3 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden /> Back to products
      </Link>

      <PageHeader
        breadcrumbs
        eyebrow="Product"
        title={name}
        description="Sales, margin and a baseline demand forecast for this SKU over the selected range."
        actions={
          <div className="flex items-center gap-2">
            <Badge tone="info" className="gap-1"><Sparkles className="h-3 w-3" /> SKU</Badge>
            <SnapshotMenu filename={`product-${productId}`} />
          </div>
        }
      />

      {perf.isError ? (
        <ErrorState onRetry={() => void perf.refetch()} />
      ) : !perf.isLoading && !row ? (
        <Card>
          <CardBody>
            <EmptyState title="No sales in range" description="This SKU has no completed orders for the selected period." />
          </CardBody>
        </Card>
      ) : (
        <div className="grid gap-4 stagger sm:grid-cols-2 lg:grid-cols-4">
          <KPICard label="GMV" value={fmt.npr(row?.gmv ?? 0)} deltaPct={row?.gmv_delta_pct ?? null} loading={perf.isLoading} />
          <KPICard label="Units" value={fmt.number(row?.units ?? 0)} deltaPct={row?.units_delta_pct ?? null} loading={perf.isLoading} />
          <KPICard label="Margin %" value={row?.margin_pct != null ? fmt.pct(row.margin_pct) : '—'} loading={perf.isLoading} />
          <KPICard label="Orders" value={fmt.number(row?.orders ?? 0)} loading={perf.isLoading} />
        </div>
      )}

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>GMV, units & orders</CardTitle>
            <ChartModeToggle
              granularity={granularity}
              onGranularityChange={setGranularity}
              seasonality={seasonality}
              onSeasonalityChange={setSeasonality}
            />
          </CardHeader>
          <CardBody>
            {series.isLoading ? (
              <Skeleton className="h-[280px] w-full" />
            ) : series.isError ? (
              <ErrorState onRetry={() => void series.refetch()} />
            ) : seasonality !== 'none' ? (
              <LineChart
                data={seasonalityChartData}
                xKey="bucket"
                series={[
                  { key: 'gmv', label: 'GMV (NPR)' },
                  { key: 'units', label: 'Units' },
                  { key: 'orders', label: 'Orders' },
                ]}
                formatX={(s) => s}
                formatValue={(v) => fmt.compact(v)}
              />
            ) : (
              <LineChart
                data={chartData}
                xKey="t"
                series={[
                  { key: 'gmv', label: 'GMV (NPR)' },
                  { key: 'units', label: 'Units' },
                  { key: 'orders', label: 'Orders' },
                ]}
                formatX={(s) => fmt.date(s, effectiveGranularity === 'hour' ? 'HH:mm' : 'd MMM')}
                formatValue={(v) => fmt.compact(v)}
              />
            )}
          </CardBody>
        </Card>
        <Card>
          <CardHeader><CardTitle>Demand forecast (14d)</CardTitle></CardHeader>
          <CardBody>
            {forecast.isLoading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : forecast.isError ? (
              <ErrorState onRetry={() => void forecast.refetch()} />
            ) : (
              <>
                <div className="mb-3 grid gap-2 sm:grid-cols-3">
                  <MiniStat label="Projected units" value={fmt.compact(forecastSummary.projectedUnits)} />
                  <MiniStat label="Projected GMV" value={fmt.npr(forecastSummary.projectedGmv)} />
                  <MiniStat label="Projected orders" value={fmt.compact(forecastSummary.projectedOrders)} />
                </div>
                <ForecastChart data={forecast.data} />
              </>
            )}
          </CardBody>
        </Card>
      </div>

      <div className="mt-4">
        <Card>
          <CardHeader>
            <CardTitle>Where it sells</CardTitle>
            <Link
              to={`/products/map?product_id=${productId}`}
              className="press inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground focus-ring"
            >
              <MapPin className="h-3.5 w-3.5" aria-hidden /> Open full map
            </Link>
          </CardHeader>
          <CardBody className="p-0">
            {wardsGeo.isLoading || wardSales.isLoading ? (
              <Skeleton className="h-[320px] w-full" />
            ) : wardsGeo.isError || wardSales.isError ? (
              <div className="p-4">
                <ErrorState onRetry={() => void (wardsGeo.isError ? wardsGeo.refetch() : wardSales.refetch())} />
              </div>
            ) : (wardsGeo.data?.features.length ?? 0) === 0 ? (
              <div className="p-4">
                <EmptyState
                  title="No ward boundaries loaded"
                  description="Ward polygons haven't been imported on the backend yet."
                />
              </div>
            ) : (
              <>
                <WardChoroplethMap
                  wardsGeo={wardsGeo.data}
                  sales={wardSales.data?.wards ?? []}
                  metric="gmv"
                  selectedWardId={selectedWardId}
                  onWardSelect={setSelectedWardId}
                  compact
                  height={320}
                  className="overflow-hidden rounded-b-lg"
                />
                {topWards.length > 0 ? (
                  <div className="flex flex-wrap items-center gap-2 border-t border-border px-5 py-3 text-xs">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Top wards
                    </span>
                    {topWards.map((w) => (
                      <span
                        key={w.id}
                        className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-2 py-0.5 font-medium"
                      >
                        {w.ward_label}
                        <span className="kpi-num text-muted-foreground">
                          {formatWardMetric('gmv', w.gmv)}
                        </span>
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="border-t border-border px-5 py-3 text-xs text-muted-foreground">
                    No mappable sales for this SKU in the selected range.
                  </p>
                )}
              </>
            )}
          </CardBody>
        </Card>
      </div>

      <div className="mt-4">
        <Card>
          <CardHeader>
            <CardTitle>Orders & customers</CardTitle>
          </CardHeader>
          <CardBody>
            <ProductDrillTables
              returnPath={returnPath}
              filters={{
                start: iso.start,
                end: iso.end,
                storeId,
                storeIds,
                productIds: [productId],
              }}
            />
          </CardBody>
        </Card>
      </div>
    </>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-surface px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="kpi-num mt-0.5 text-sm font-semibold">{value}</p>
    </div>
  );
}
